import React from 'react';
import { Box, useTheme } from '@mui/material';
import { FiltersProvider, useFilters } from '../../contexts/FiltersContext';
import { AnalyticsDataProvider } from '../../contexts/AnalyticsDataContext';
import { AnalyticsRefreshProvider } from '../../contexts/AnalyticsRefreshContext';
import SidePanel from '../../components/analytics/SidePanel/SidePanelOptimized';
import TopPanel from '../../components/analytics/TopPanel/TopPanel';
import Analytics from './Analytics';
import { SplitViewProvider, useSplitView } from '../../contexts/SplitViewContext';

const AnalyticsPageContent: React.FC = () => {
  const theme = useTheme();
  const { isSplitView } = useSplitView();
  const {
    timeFilter,
    fromToDate,
    licenseeData,
    subCompanyData,
    branchData,
    subCustomerData,
    countryOriginData,
    countryDestinationData,
    cityOriginData,
    cityDestinationData,
    transportModeData,
  } = useFilters();

  return (
    <Box sx={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      '@media print': {
        height: 'auto',
        overflow: 'visible',
      },
    }}>
      {/* Side Panel - Filter Sidebar - Hidden in split view */}
      {!isSplitView && <SidePanel />}

      {/* Main Content Area */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '@media print': {
          overflow: 'visible',
        },
      }}>
        {/* Top Panel - Time Filters and Export - Visible only in single view at the top */}
        {!isSplitView && <TopPanel pageType="analytics" />}

        {/* Analytics Content - KPI Cards, Tabs, Charts, Tables */}
        <Box id="page-print-area" sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          '@media print': {
            overflow: 'visible',
            display: 'block',
          },
        }}>
          {/* First View (Left) - Uses Root Providers */}
          <Box id={isSplitView ? 'split-view-left' : undefined} sx={{
            flex: 1,
            overflow: 'hidden',
            borderRight: isSplitView ? `1px solid ${theme.palette.divider}` : 'none',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            '@media print': {
              overflow: 'visible',
              display: 'block',
            },
          }}>
            {isSplitView && <TopPanel pageType="analytics" showSplitViewControls={false} printAreaId="split-view-left" />}
            <Box sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              '@media print': {
                overflow: 'visible',
                display: 'block',
              },
            }}>
              <Analytics />
            </Box>
          </Box>

          {/* Second View (Right) - Uses Scoped Providers */}
          {isSplitView && (
            <Box id="split-view-right" sx={{
              flex: 1,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              animation: 'fadeIn 0.5s ease-in-out',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <FiltersProvider
                initialTimeFilter={timeFilter}
                initialFromToDate={fromToDate}
                initialLicenseeData={licenseeData}
                initialSubCompanyData={subCompanyData}
                initialBranchData={branchData}
                initialSubCustomerData={subCustomerData}
                initialCountryOriginData={countryOriginData}
                initialCountryDestinationData={countryDestinationData}
                initialCityOriginData={cityOriginData}
                initialCityDestinationData={cityDestinationData}
                initialTransportModeData={transportModeData}
              >
                <AnalyticsRefreshProvider>
                  <AnalyticsDataProvider>
                    <TopPanel pageType="analytics" printAreaId="split-view-right" />
                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <Analytics skipDefaultFilterFetch />
                    </Box>
                  </AnalyticsDataProvider>
                </AnalyticsRefreshProvider>
              </FiltersProvider>
            </Box>
          )}
        </Box>
      </Box>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}
      </style>
    </Box>
  );
};

const AnalyticsPage: React.FC = () => {
  return (
    <SplitViewProvider>
      <FiltersProvider>
        <AnalyticsRefreshProvider>
          <AnalyticsDataProvider>
            <AnalyticsPageContent />
          </AnalyticsDataProvider>
        </AnalyticsRefreshProvider>
      </FiltersProvider>
    </SplitViewProvider>
  );
};

export default AnalyticsPage;
